import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  listDetailLayoutDetailRecipe,
  listDetailLayoutListRecipe,
  listDetailLayoutRecipe,
  type ListDetailLayoutRecipeProps,
} from '../recipes/list-detail.recipe';

export interface ListDetailLayoutProps
  extends React.HTMLAttributes<HTMLDivElement>,
    ListDetailLayoutRecipeProps {
  /** The left column: a ListToolbar over bands and rows. Flush — no card. */
  list: React.ReactNode;
  /** The right column: the record's card, or what to show when there is none. */
  detail: React.ReactNode;
  /**
   * Inside a page that already insets its content, as the dashboard's does:
   * the list takes back 16px so its toolbar and bands line up with the
   * title. Pair with `<ListColumn bleed>` for the same screen's Table view.
   */
  bleed?: boolean;
}

/**
 * A grouped list beside the record picked from it.
 *
 *   <Page fill="lg">
 *     <Tabs fill="lg">
 *       <TabsContent value="reports" fill="lg">
 *         <ListDetailLayout scroll list={…} detail={<ReportCard />} />
 *
 * The list is 400 wide from `lg` up and full width below, where the two
 * stack and the page scrolls as one. With `scroll`, and a frame above that
 * hands it a height, each column scrolls on its own on a desktop instead.
 * The inspections and trucks screens both drew this with a dozen classes on
 * three Stacks, and the skeleton drew it a fourth time.
 */
export const ListDetailLayout = React.forwardRef<
  HTMLDivElement,
  ListDetailLayoutProps
>(function ListDetailLayout(
  { list, detail, scroll, bleed, className, ...props },
  ref
) {
  return (
    <div
      ref={ref}
      className={cn(listDetailLayoutRecipe({ scroll }), className)}
      {...props}
    >
      <div className={listDetailLayoutListRecipe({ scroll, bleed })}>
        {list}
      </div>
      <div className={listDetailLayoutDetailRecipe({ scroll })}>{detail}</div>
    </div>
  );
});
